// ProposalActionPage — /p/:token magic-link landing for Wave 3.
//
// UNAUTHENTICATED. The token in the URL is the credential. Mount under
// `/p/:token` in App.jsx; do NOT wrap in ProtectedRoute.
//
// On click, POSTs the token + action to /v1/proposals/:id/response.
// No GET-as-action — defeats Outlook Safe Links and similar prefetchers.
//
// Also embeddable: when running inside an iframe (window !== window.parent),
// posts vennio:proposal:* events to the parent on every terminal state so that
// @vennio/proposals-widget consumers can react to accept/counter/reject/error.
import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import styles from './ProposalActionPage.module.css';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

const STATE = {
  LOADING: 'loading',
  NOT_FOUND: 'not_found',
  EXPIRED: 'expired',
  USED: 'used',
  CALENDAR_DISCONNECTED: 'calendar_disconnected',
  READY: 'ready',
  COUNTERING: 'countering',
  ACCEPTED: 'accepted',
  COUNTERED: 'countered',
  REJECTED: 'rejected',
  ERROR: 'error',
};

function formatRange(slot) {
  const start = new Date(slot.start_time);
  const end = new Date(slot.end_time);
  const dateStr = start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const timeStr = `${start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} – ${end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
  return { dateStr, timeStr };
}

// Emit a postMessage to the parent window when embedded. Target origin is '*'
// because third-party embeds cannot enumerate parent origins; the widget
// itself verifies event.origin on the receive side.
function emitParent(type, payload) {
  if (typeof window === 'undefined') return;
  if (window === window.parent) return;
  try {
    window.parent.postMessage({ type: `vennio:proposal:${type}`, ...payload }, '*');
  } catch {
    // Cross-origin failures are non-fatal — the embedded page still works.
  }
}

const emptyCounterRow = () => ({ start: '', end: '', preference: '' });

export default function ProposalActionPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const actionHint = searchParams.get('action'); // cosmetic pre-highlight only

  const [state, setState] = useState(STATE.LOADING);
  const [data, setData] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [counterSlots, setCounterSlots] = useState([emptyCounterRow()]);
  const [counterMessage, setCounterMessage] = useState('');

  useEffect(() => {
    document.title = 'Vennio — respond to proposal';
  }, []);

  useEffect(() => {
    if (!token) {
      setState(STATE.NOT_FOUND);
      emitParent('not_found', {});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/v1/proposals/by-token?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (res.status === 404) {
          setState(STATE.NOT_FOUND);
          emitParent('not_found', {});
          return;
        }
        if (res.status === 410) {
          setState(STATE.EXPIRED);
          emitParent('expired', {});
          return;
        }
        if (!res.ok) {
          setState(STATE.ERROR);
          emitParent('error', { message: `error_${res.status}` });
          return;
        }
        const body = await res.json();
        setData(body);
        const proposalId = body.proposal?.id;
        if (body.token?.used) {
          setState(STATE.USED);
          emitParent('used', { proposalId });
        } else if (body.participant?.calendar_connected === false) {
          setState(STATE.CALENDAR_DISCONNECTED);
          emitParent('calendar_disconnected', { proposalId });
        } else {
          setState(STATE.READY);
        }
      } catch (err) {
        if (!cancelled) {
          setErrorDetail(err.message);
          setState(STATE.ERROR);
          emitParent('error', { message: err.message });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (action, opts = {}) => {
    if (submitting) return;
    setSubmitting(true);
    setErrorDetail(null);
    try {
      const slot = data?.proposal?.slots?.[0];
      const body = { token, action };
      if (action === 'accept' && slot) body.selected_slot_id = slot.id;
      if (action === 'counter') {
        body.counter_slots = opts.counter_slots;
        if (opts.message) body.message = opts.message;
      }
      const res = await fetch(`${API_URL}/v1/proposals/${data.proposal.id}/response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        setErrorDetail(json.detail || json.message || `error_${res.status}`);
        if (res.status === 401 || (res.status === 409 && /token/i.test(json.detail || ''))) {
          setState(STATE.USED);
          emitParent('used', { proposalId: data.proposal.id });
        } else {
          emitParent('error', {
            proposalId: data.proposal.id,
            message: json.detail || json.message || `error_${res.status}`,
          });
        }
        setSubmitting(false);
        return;
      }
      const proposalId = data.proposal.id;
      if (action === 'accept') {
        setState(STATE.ACCEPTED);
        emitParent('accepted', {
          proposalId,
          slot: slot
            ? { id: slot.id, start_time: slot.start_time, end_time: slot.end_time }
            : null,
        });
      } else if (action === 'counter') {
        setState(STATE.COUNTERED);
        emitParent('countered', { proposalId, counterSlots: opts.counter_slots });
      } else {
        setState(STATE.REJECTED);
        emitParent('rejected', { proposalId });
      }
    } catch (err) {
      setErrorDetail(err.message);
      emitParent('error', { proposalId: data?.proposal?.id, message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const updateCounterRow = (idx, patch) => {
    setCounterSlots(rows => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addCounterRow = () => {
    setCounterSlots(rows => (rows.length >= 10 ? rows : [...rows, emptyCounterRow()]));
  };
  const removeCounterRow = (idx) => {
    setCounterSlots(rows => (rows.length <= 1 ? rows : rows.filter((_, i) => i !== idx)));
  };

  const submitCounter = () => {
    const cleaned = counterSlots
      .map(r => {
        if (!r.start || !r.end) return null;
        const startISO = new Date(r.start).toISOString();
        const endISO = new Date(r.end).toISOString();
        if (Number.isNaN(Date.parse(startISO)) || Number.isNaN(Date.parse(endISO))) return null;
        const out = { start: startISO, end: endISO };
        if (r.preference) {
          const p = parseInt(r.preference, 10);
          if (Number.isFinite(p) && p >= 1) out.preference = p;
        }
        return out;
      })
      .filter(Boolean);
    if (cleaned.length === 0) {
      setErrorDetail('Add at least one alternative time before submitting.');
      return;
    }
    submit('counter', { counter_slots: cleaned, message: counterMessage || undefined });
  };

  return (
    <div className={styles.page}>
      <div className={styles.frame}>
        <p className={styles.eyebrow}>Vennio · Proposal</p>

        {state === STATE.LOADING && <p className={styles.body}>Loading…</p>}

        {state === STATE.NOT_FOUND && (
          <>
            <h1 className={styles.h1}>This link is invalid</h1>
            <p className={styles.body}>The proposal could not be found. Ask the organiser to resend the invitation.</p>
          </>
        )}

        {state === STATE.EXPIRED && (
          <>
            <h1 className={styles.h1}>This proposal has expired</h1>
            <p className={styles.body}>The window to respond has closed.</p>
          </>
        )}

        {state === STATE.USED && (
          <>
            <h1 className={styles.h1}>You already responded</h1>
            <p className={styles.body}>This proposal has been actioned. View the booking in the portal.</p>
            {data?.proposal?.id && (
              <Link to={`/proposals/${data.proposal.id}`} className={styles.tertiaryLink}>View in portal</Link>
            )}
          </>
        )}

        {state === STATE.CALENDAR_DISCONNECTED && (
          <>
            <h1 className={styles.h1}>Connect your calendar to respond</h1>
            <p className={styles.body}>
              {data?.originator?.name || 'Someone'} proposed a meeting with you. Vennio needs access to your calendar before we can accept on your behalf.
            </p>
            <a href="/calendar/callback" className={styles.btnPrimary}>Connect calendar</a>
            <p className={styles.note}>This link will stay valid — return here once connected.</p>
          </>
        )}

        {state === STATE.READY && data && (
          <>
            <h1 className={styles.h1}>{data.originator?.name || 'Someone'} would like to meet</h1>
            {data.proposal.message && (
              <div className={styles.message}>"{data.proposal.message}"</div>
            )}
            <p className={styles.label}>Proposed times</p>
            <ul className={styles.slotList}>
              {(data.proposal.slots || []).map(slot => {
                const { dateStr, timeStr } = formatRange(slot);
                return (
                  <li key={slot.id} className={styles.slot}>
                    <span className={styles.slotDate}>{dateStr}</span>
                    <span className={styles.slotTime}>{timeStr}</span>
                  </li>
                );
              })}
            </ul>
            <div className={styles.actions}>
              <button
                type="button"
                className={`${styles.btnPrimary} ${actionHint === 'accept' ? styles.hinted : ''}`}
                disabled={submitting}
                onClick={() => submit('accept')}
              >
                Accept
              </button>
              <button
                type="button"
                className={`${styles.btnSecondary} ${actionHint === 'decline' ? styles.hinted : ''}`}
                disabled={submitting}
                onClick={() => submit('reject')}
              >
                Decline
              </button>
            </div>
            <button
              type="button"
              className={styles.tertiaryLink}
              onClick={() => { setErrorDetail(null); setState(STATE.COUNTERING); }}
            >
              Propose different times
            </button>
            {errorDetail && <p className={styles.error}>{errorDetail}</p>}
          </>
        )}

        {state === STATE.COUNTERING && data && (
          <>
            <h1 className={styles.h1}>Propose different times</h1>
            <p className={styles.body}>Suggest up to 10 alternative slots. The organiser will choose from these.</p>
            <p className={styles.label}>Your alternatives</p>
            <ul className={styles.counterList}>
              {counterSlots.map((row, idx) => (
                <li key={idx} className={styles.counterRow}>
                  <div className={styles.counterFields}>
                    <label className={styles.counterField}>
                      <span className={styles.counterFieldLabel}>Start</span>
                      <input
                        type="datetime-local"
                        className={styles.counterInput}
                        value={row.start}
                        onChange={(e) => updateCounterRow(idx, { start: e.target.value })}
                      />
                    </label>
                    <label className={styles.counterField}>
                      <span className={styles.counterFieldLabel}>End</span>
                      <input
                        type="datetime-local"
                        className={styles.counterInput}
                        value={row.end}
                        onChange={(e) => updateCounterRow(idx, { end: e.target.value })}
                      />
                    </label>
                  </div>
                  {counterSlots.length > 1 && (
                    <button
                      type="button"
                      className={styles.counterRemove}
                      onClick={() => removeCounterRow(idx)}
                      aria-label={`Remove alternative ${idx + 1}`}
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {counterSlots.length < 10 && (
              <button type="button" className={styles.tertiaryLink} onClick={addCounterRow}>
                + Add another time
              </button>
            )}
            <label className={styles.counterField}>
              <span className={styles.counterFieldLabel}>Message (optional)</span>
              <textarea
                className={styles.counterTextarea}
                rows={3}
                value={counterMessage}
                onChange={(e) => setCounterMessage(e.target.value)}
                placeholder="A note to the organiser…"
              />
            </label>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={submitting}
                onClick={submitCounter}
              >
                Send counter-proposal
              </button>
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={submitting}
                onClick={() => { setErrorDetail(null); setState(STATE.READY); }}
              >
                Back
              </button>
            </div>
            {errorDetail && <p className={styles.error}>{errorDetail}</p>}
          </>
        )}

        {state === STATE.ACCEPTED && (
          <>
            <h1 className={styles.h1}>Accepted</h1>
            <p className={styles.body}>The meeting is on your calendar. You'll get a confirmation email shortly.</p>
          </>
        )}

        {state === STATE.COUNTERED && (
          <>
            <h1 className={styles.h1}>Counter-proposal sent</h1>
            <p className={styles.body}>The organiser has been notified of your alternative times.</p>
          </>
        )}

        {state === STATE.REJECTED && (
          <>
            <h1 className={styles.h1}>Declined</h1>
            <p className={styles.body}>The organiser has been notified.</p>
          </>
        )}

        {state === STATE.ERROR && (
          <>
            <h1 className={styles.h1}>Something went wrong</h1>
            <p className={styles.body}>{errorDetail || 'Please try again in a moment.'}</p>
          </>
        )}
      </div>
    </div>
  );
}
